import React, { useEffect, useState } from 'react';
import { TypographyStyledHeader, BoxStyled, GridStyled } from './style';
import { TabPanel } from '../../shared/components/tabPanel/index';
import { useDispatch } from 'react-redux';
import { AgeView } from './AgeView';
import { DOBView } from './DOBView';
import { ImperialView } from './ImperialView';
import { MetricView } from './MetricView';
import { PatientType } from './PatientType';
import { useAppSelector } from '../../store/hooks';
import { metaDataSelector } from '../../store/globalSelectors';
import { RedirectButton } from './RedirectButton';
import { BMICard } from '../../shared/components/bmiCard';
import {
  resetPatientData,
  setPatientInformation,
  setRouteData
} from '../../store/slices/metaDataSlice';
import {
  getWeightLossPercentage,
  getBMI
} from '../../shared/utils/utilFunctions';
import bmiIcon from '../../assets/icons/bmiIcon.svg';
import lossPercentage from '../../assets/icons/lossPercentage.svg';
import { NavigationButtons } from '../../shared/components/navigationButtons';
import { useNavigate } from 'react-router-dom';
import { Box, Button } from '@mui/material';

const PatientInformation = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    patientInformation,
    alternativeMeasures,
    routeData,
    malnutritionScreening: { glim }
  } = useAppSelector(metaDataSelector);
  const [alternateBtnDisabled, setAlternateBtnDisabled] = useState(true);
  const [nextIconDisabled, setNextIconDisabled] = useState(true);
  const [routeValue, setRouteValue] = useState('');
  const [patientBMI, setPatientBMI] = useState(patientInformation?.bmi);
  const [patientWeightLoss, setPatientWeightLoss] = useState(
    patientInformation?.lossPercentage
  );

  useEffect(() => {
    setRouteValue(getNextRoute());
  }, [routeData]);

  useEffect(() => {
    const info = patientInformation;
    if (info?.type && info?.gender && (info?.age?.years || info?.age?.months)) {
      info?.metric?.weight || info?.metric?.estimatedDryWeight
        ? setAlternateBtnDisabled(false)
        : setAlternateBtnDisabled(true);
      if (
        info?.metric?.weight &&
        (info?.bmi || alternativeMeasures?.estBMI_ulna)
      ) {
        setNextIconDisabled(false);
        dispatch(
          setRouteData({
            ...routeData,
            fillMandatoryFields: true
          })
        );
      } else {
        setNextIconDisabled(true);
        dispatch(
          setRouteData({
            ...routeData,
            fillMandatoryFields: false
          })
        );
      }
    } else {
      setNextIconDisabled(true);
      dispatch(
        setRouteData({
          ...routeData,
          fillMandatoryFields: false
        })
      );
      setAlternateBtnDisabled(true);
    }
  }, [
    alternativeMeasures?.estBMI_ulna,
    patientInformation?.bmi,
    patientInformation?.type,
    patientInformation?.gender,
    patientInformation?.age,
    patientInformation?.metric?.weight,
    patientInformation?.metric?.estimatedDryWeight
  ]);

  useEffect(() => {
    const metric = patientInformation?.metric;
    const weight = metric?.estimatedDryWeight || metric?.weight;
    let loss = 0;
    if (weight > 0 && metric?.usualWeight > 0) {
      loss = getWeightLossPercentage(weight, metric.usualWeight);
    }
    dispatch(
      setPatientInformation({
        ...patientInformation,
        lossPercentage: loss
      })
    );
    setPatientWeightLoss(loss);
  }, [
    patientInformation?.metric?.weight,
    patientInformation?.metric?.estimatedDryWeight,
    patientInformation?.metric?.usualWeight
  ]);

  useEffect(() => {
    let newBmiVal = 0;
    const metric = patientInformation?.metric;

    if (
      metric?.height > 0 &&
      (metric?.estimatedDryWeight > 0 || metric?.weight > 0)
    ) {
      newBmiVal = getBMI(
        metric?.estimatedDryWeight || metric?.weight,
        metric?.height
      );
    }
    dispatch(
      setPatientInformation({
        ...patientInformation,
        bmi: newBmiVal
      })
    );
    setPatientBMI(newBmiVal);
  }, [
    patientInformation?.metric?.height,
    patientInformation?.metric?.weight,
    patientInformation?.metric?.estimatedDryWeight
  ]);

  useEffect(() => {
    const age = patientInformation?.age?.years;
    if (age) {
      dispatch(
        setPatientInformation({
          ...patientInformation,
          type: age > 17 ? 'ADULT' : 'PAEDIATRIC'
        })
      );
    }
  }, [patientInformation?.age?.years]);

  const getNextRoute = () => {
    if (patientInformation?.type === 'PAEDIATRIC')
      return '/estimating-requirements/paediatric-energy';
    else if (routeData?.lastClickedTile === 'NUTRITIONAL REQUIREMENTS')
      return '/nextSteps';
    else if (routeData?.lastClickedTile === 'MUSCLE ASSESSMENT')
      return '/muscle-screening';
    else if (routeData?.lastClickedTile === 'MALNUTRITION ASSESSMENT')
      return '/malnutrition-screening';
    else return '/nextSteps'; //Default route value
  };

  return (
    <>
      <BoxStyled className="generic-container">
        <TypographyStyledHeader>{`PATIENT INFORMATION`}</TypographyStyledHeader>

        <Button
          variant="contained"
          onClick={() => dispatch(resetPatientData())}
          data-testid="dataRefresh"
          sx={{
            borderRadius: '1.25rem',
            height: '3.125rem',
            margin: '1.25rem',
            background: '#004F71',
            ':disabled': { color: 'black' }
          }}
        >
          NEW PATIENT
        </Button>

        <GridStyled container>
          <PatientType />
        </GridStyled>

        <TabPanel
          tabs={['DATE OF BIRTH', 'AGE']}
          panelData={[<DOBView key={0} />, <AgeView key={1} />]}
        />

        <TabPanel
          tabs={['METRIC', 'IMPERIAL']}
          panelData={[<MetricView key={0} />, <ImperialView key={1} />]}
        />

        {patientInformation?.type === 'ADULT' &&
          patientInformation?.age?.years > 17 && (
            <RedirectButton
              label="ALTERNATIVE MEASURES"
              disabled={alternateBtnDisabled}
              onClick={() => {
                patientInformation?.type !== 'PAEDIATRIC' &&
                  navigate('/alternative-measures');
              }}
            />
          )}

        <Box sx={{ width: '100%', margin: '1.25rem' }}>
          <BMICard
            icon={lossPercentage}
            label1="ESTIMATED WEIGHT LOSS"
            label2={`${patientWeightLoss}%`}
          />
        </Box>

        <Box sx={{ width: '100%', margin: '1.25rem' }}>
          <BMICard
            icon={bmiIcon}
            label1="BMI"
            label2={`${patientBMI} kg/m`}
            unit={'<sup>2</sup>'}
          />
        </Box>
      </BoxStyled>
      <NavigationButtons
        nextDisabled={nextIconDisabled}
        nextRoute={routeValue}
      />
    </>
  );
};

export { PatientInformation };
